import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useCallback,
  useState,
  createContext,
  useContext,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { TargetAndTransition } from 'motion/react';
import './Dialog.css';

export interface DialogProps {
  children: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Called when the user requests to close the dialog (e.g. pressing Escape or
   * clicking the header close button).
   * If provided, the dialog will NOT auto-close; you are responsible for closing it via ref.
   */
  onRequestClose?: () => void;
  /**
   * When true uses showModal (modal top-layer). When false uses show() (non-modal, no backdrop).
   * Defaults to true.
   */
  modal?: boolean;
}

export interface DialogHandle {
  show: () => void;
  close: () => void;
}

const MotionDialog = motion.create('dialog');

/**
 * Body scroll lock with stacking support and scrollbar-width compensation.
 *
 * Native modal <dialog> elements make content behind inert but do NOT prevent
 * the page from scrolling. We freeze <html> overflow while any modal Dialog is
 * open, and pad the right edge by the scrollbar width to avoid layout shift
 * when the vertical scrollbar disappears.
 *
 * A module-level counter handles stacked dialogs so the page only unlocks once
 * the last one closes.
 */
let scrollLockCount = 0;
let scrollLockSnapshot: {
  htmlOverflow: string;
  bodyPaddingRight: string;
} | null = null;

function acquireScrollLock() {
  if (typeof document === 'undefined') return;
  scrollLockCount += 1;
  if (scrollLockCount > 1) return;

  const html = document.documentElement;
  const body = document.body;
  const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);

  scrollLockSnapshot = {
    htmlOverflow: html.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
  };

  html.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    const currentPadding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
  }
}

function releaseScrollLock() {
  if (typeof document === 'undefined') return;
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount > 0) return;

  const html = document.documentElement;
  const body = document.body;
  if (scrollLockSnapshot) {
    html.style.overflow = scrollLockSnapshot.htmlOverflow;
    body.style.paddingRight = scrollLockSnapshot.bodyPaddingRight;
    scrollLockSnapshot = null;
  } else {
    html.style.overflow = '';
    body.style.paddingRight = '';
  }
}

type BackdropTarget = TargetAndTransition & {
  ['--dialog-backdrop-opacity']?: number;
  ['--dialog-backdrop-blur']?: number;
};

const dialogOpenState: BackdropTarget = {
  opacity: 1,
  filter: 'blur(0px)',
  rotateX: 0,
  rotateY: 0,
  z: 0,
  ['--dialog-backdrop-opacity']: 0.5,
  ['--dialog-backdrop-blur']: 2,
  transition: {
    duration: 0.18,
    ease: [0.22, 1, 0.36, 1],
    opacity: {
      duration: 0.16,
      ease: [0.22, 1, 0.36, 1],
    },
    ['--dialog-backdrop-opacity']: {
      duration: 0.18,
      ease: [0.22, 1, 0.36, 1],
    },
    ['--dialog-backdrop-blur']: {
      duration: 0.18,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const dialogInitialState: BackdropTarget = {
  opacity: 0,
  filter: 'blur(4px)',
  y: 8,
  scale: 0.985,
  transformPerspective: 500,
  ['--dialog-backdrop-opacity']: 0,
  ['--dialog-backdrop-blur']: 0,
  transition: {
    duration: 0.12,
    ease: [0.4, 0, 1, 1],
    ['--dialog-backdrop-opacity']: {
      duration: 0.12,
      ease: [0.4, 0, 1, 1],
    },
    ['--dialog-backdrop-blur']: {
      duration: 0.12,
      ease: [0.4, 0, 1, 1],
    },
  },
};

export interface DialogSectionProps {
  children?: React.ReactNode;
  className?: string;
}

function joinClassNames(base: string, extra?: string) {
  return extra ? `${base} ${extra}` : base;
}

type DialogContextValue = { requestClose: () => void } | null;
const DialogContext = createContext<DialogContextValue>(null);

export type DialogHeaderProps = DialogSectionProps & {
  showClose?: boolean;
  closeAriaLabel?: string;
};

function DialogHeader({
  children,
  className,
  showClose = true,
  closeAriaLabel = 'Close dialog',
}: DialogHeaderProps) {
  const ctx = useContext(DialogContext);
  return (
    <header className={joinClassNames('dialog__header', className)}>
      {children}
      {showClose && (
        <button
          className="dialog__close"
          aria-label={closeAriaLabel}
          onClick={() => ctx?.requestClose && ctx.requestClose()}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden
          >
            <path
              d="M6 18L18 6M6 6l12 12"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </header>
  );
}

function DialogContent({ children, className }: DialogSectionProps) {
  return <div className={joinClassNames('dialog__content', className)}>{children}</div>;
}

function DialogFooter({ children, className }: DialogSectionProps) {
  return <footer className={joinClassNames('dialog__footer', className)}>{children}</footer>;
}

/**
 * Observes a content wrapper for size changes and animates the dialog's
 * height using the FLIP technique. `isActive` must be true when both
 * elements are mounted in the DOM.
 */
function useAnimatedHeight(
  dialogRef: React.RefObject<HTMLDialogElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
  isActive: boolean,
) {
  const prevHeight = useRef<number | null>(null);
  const animating = useRef(false);
  const settled = useRef(false);

  useEffect(() => {
    if (!isActive) {
      prevHeight.current = null;
      animating.current = false;
      settled.current = false;
      return;
    }

    const dialog = dialogRef.current;
    const content = contentRef.current;
    if (!dialog || !content) return;

    const settleTimer = setTimeout(() => {
      prevHeight.current = dialog.offsetHeight;
      settled.current = true;
    }, 300);

    const observer = new ResizeObserver(() => {
      if (!settled.current || animating.current) return;

      const newHeight = dialog.offsetHeight;
      const oldHeight = prevHeight.current;

      if (oldHeight !== null && Math.abs(oldHeight - newHeight) > 1) {
        animating.current = true;

        dialog.style.transition = 'none';
        dialog.style.height = `${oldHeight}px`;
        dialog.style.overflow = 'clip';
        void dialog.offsetHeight;
        dialog.style.transition = 'height 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
        dialog.style.height = `${newHeight}px`;

        const onEnd = (e: TransitionEvent) => {
          if (e.propertyName !== 'height') return;
          dialog.removeEventListener('transitionend', onEnd);
          dialog.style.height = '';
          dialog.style.overflow = '';
          dialog.style.transition = '';
          prevHeight.current = dialog.offsetHeight;
          animating.current = false;
        };
        dialog.addEventListener('transitionend', onEnd);
      } else {
        prevHeight.current = newHeight;
      }
    });

    observer.observe(content);

    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
      animating.current = false;
    };
  }, [isActive, dialogRef, contentRef]);
}

const DialogRoot = forwardRef<DialogHandle, DialogProps>(function Dialog(
  { children, ariaLabel, className, style, onRequestClose, modal = true },
  ref,
) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useAnimatedHeight(dialogRef, contentWrapperRef, isOpen);

  const requestOpen = () => {
    setIsExiting(false);
    setIsOpen(true);
  };

  const handleRequestClose = useCallback(() => {
    if (onRequestClose) {
      onRequestClose();
    } else {
      setIsExiting(true);
      setIsOpen(false);
    }
  }, [onRequestClose]);

  const internalClose = useCallback(() => {
    setIsExiting(true);
    setIsOpen(false);
  }, []);

  useImperativeHandle(ref, () => ({
    show: requestOpen,
    close: internalClose,
  }));

  useEffect(() => {
    if (!dialogRef.current) return;

    const node = dialogRef.current;

    const onCancel = (event: Event) => {
      event.preventDefault();
      handleRequestClose();
    };

    const onClose = () => {
      setIsOpen(false);
    };

    node.addEventListener('cancel', onCancel);
    node.addEventListener('close', onClose);

    return () => {
      node.removeEventListener('cancel', onCancel);
      node.removeEventListener('close', onClose);
    };
  }, [handleRequestClose]);

  useEffect(() => {
    if (isOpen && dialogRef.current && !dialogRef.current.open) {
      if (modal) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.show();
      }
    }
  }, [isOpen, modal]);

  // Lock background page scroll while a modal dialog is open. Native <dialog>
  // makes content behind inert but does not freeze scrolling.
  useEffect(() => {
    if (!isOpen || !modal) return;
    acquireScrollLock();
    return () => {
      releaseScrollLock();
    };
  }, [isOpen, modal]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <MotionDialog
            ref={dialogRef}
            className={joinClassNames('dialog', className)}
            aria-label={ariaLabel}
            data-state={isExiting ? 'exiting' : isOpen ? 'open' : 'closed'}
            style={{
              transformPerspective: 500,
              willChange: 'transform, opacity, filter',
              ...style,
            }}
            initial={dialogInitialState}
            animate={dialogOpenState}
            exit={dialogInitialState}
            onAnimationComplete={() => {
              if (!isOpen) {
                dialogRef.current?.close();
                setIsExiting(false);
              }
            }}
          >
            <div ref={contentWrapperRef}>
              <DialogContext.Provider value={{ requestClose: handleRequestClose }}>
                {children}
              </DialogContext.Provider>
            </div>
          </MotionDialog>
        )}
      </AnimatePresence>
    </>
  );
});

export type DialogCompound = React.ForwardRefExoticComponent<
  DialogProps & React.RefAttributes<DialogHandle>
> & {
  Header: React.FC<DialogHeaderProps>;
  Content: React.FC<DialogSectionProps>;
  Footer: React.FC<DialogSectionProps>;
};

export const Dialog: DialogCompound = Object.assign(DialogRoot, {
  Header: DialogHeader,
  Content: DialogContent,
  Footer: DialogFooter,
});
